import { useEffect } from 'react';
import { useSenzaRete } from './useSenzaRete';
import { useMultiplayerStore } from '../store/multiplayer-store';
import { risvegliaSocket } from '../services/socket';

/**
 * L'altra meta' della scadenza della presenza: quella che si sa SUBITO.
 *
 * Il socket si accorge di una rete sparita per via del suo battito — ping 25 s,
 * timeout 20 s, nessun override lato server — quindi nel caso peggiore passano
 * ~45 secondi prima che `disconnect` arrivi, e solo DOPO parte l'attesa di
 * `ATTESA_INVALIDAZIONE_PRESENZA_MS`. Per tutto quel tempo un telefono portato
 * fuori rete mostrava i tavoli e i collegati dell'ultima connessione, con i
 * bottoni «Siediti» accesi.
 *
 * Il sistema operativo pero' lo sa all'istante, e lo dice. Qui si ascolta quello.
 *
 * Nessun rimbalzo, di proposito, ed e' la stessa scelta (e lo stesso motivo) di
 * `useSenzaRete`: il socket in riconnessione sfarfalla davvero, il SO no.
 *
 * ATTENZIONE AL LIMITE, che e' quello di `useSenzaRete` e non si puo' superare
 * da qui: `navigator.onLine === true` vuol dire «attaccato a UNA rete», non
 * «internet raggiungibile». Portale captive, server giu', DNS rotto: per il
 * browser sono tutti «online». Quella meta' resta coperta dall'altro innesco —
 * il `disconnect` del socket, che la vede eccome, solo piu' tardi.
 *
 * Va chiamato UNA volta sola, in `App`. Non e' un hook per i componenti che
 * disegnano le liste: quelli leggono `presenzaScaduta` e basta.
 */
export function useScadenzaPresenza(): void {
  const senzaRete = useSenzaRete();

  useEffect(() => {
    /*
     * `getState()` e non un selettore: questo effetto non deve rinascere perche'
     * lo store si e' mosso.
     */
    if (senzaRete) {
      useMultiplayerStore.getState().invalidaPresenza();
      return;
    }

    /*
     * E il verso opposto, che e' la meta' meno ovvia — e un difetto trovato nel
     * browser, non a tavolino.
     *
     * Avere di nuovo una rete NON rialza la presenza da se': a rialzarla sono
     * `onConnect` e `onRientro`, e un buco di rete breve non uccide il socket.
     * Quindi dopo un buco di due secondi si restava con la bandiera alzata su un
     * socket VIVO: le liste vuote e i bottoni disarmati, con scritto
     * «connessione al server...» a tempo indeterminato, e nessun evento in
     * arrivo che potesse smentirlo.
     *
     * `risvegliaSocket` e' esattamente la domanda da fare qui, ed esiste gia'
     * per il ritorno in primo piano: su un socket vivo sonda il server e
     * consegna l'esito a `onRientro`, su uno morto riconnette e si passa da
     * `onConnect`. Entrambe le strade abbassano la bandiera e riallineano le
     * liste; e le guardie (handshake in volo, sessione evinta, riconnessione
     * spenta di proposito) sono le sue, non da riscrivere qui.
     *
     * Solo a presenza scaduta: senza questa condizione ogni rientro di rete
     * sarebbe una sonda in piu', anche quando non c'e' niente da riparare.
     */
    if (useMultiplayerStore.getState().presenzaScaduta) void risvegliaSocket();
  }, [senzaRete]);
}
